import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import DependentsFieldArray from './DependentsFieldArray';
import receptionService from '../../../services/receptionService';
import MemberService from '../../../services/memberService';
import ReceptionModalShell, { ReceptionModalSection, ReceptionFormField } from '../ui/ReceptionModalShell';
import {
  ReceptionSearchAnchor,
  ReceptionFloatingResults,
  ReceptionFloatingResultItem
} from '../ui';

export default function HouseholdRegistrationModal({ show, onHide, companies = [], toast, onCreated }) {
  const [companyId, setCompanyId] = useState('');
  const [agentQuery, setAgentQuery] = useState('');
  const [agentSearchLoading, setAgentSearchLoading] = useState(false);
  const [agentSearchResults, setAgentSearchResults] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [dependents, setDependents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');

  const canSubmit = useMemo(
    () => !!companyId && !!selectedAgent?.id && !saving,
    [companyId, selectedAgent, saving]
  );

  const reset = () => {
    setCompanyId('');
    setAgentQuery('');
    setAgentSearchResults([]);
    setSelectedAgent(null);
    setDependents([]);
    setErrors({});
    setGlobalError('');
  };

  const close = () => {
    if (saving) return;
    reset();
    onHide();
  };

  const validate = () => {
    const nextErrors = {};
    if (!companyId) nextErrors.company_id = 'La société est obligatoire.';
    if (!selectedAgent?.id) nextErrors.agent_id = 'Sélectionnez un agent.';

    dependents.forEach((dep, idx) => {
      if (!dep.nom?.trim()) nextErrors[`dependents.${idx}.nom`] = 'Nom requis';
      if (!dep.prenom?.trim()) nextErrors[`dependents.${idx}.prenom`] = 'Prénom requis';
      if (!dep.type || dep.type === 'AGENT') nextErrors[`dependents.${idx}.type`] = 'Type invalide';
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  useEffect(() => {
    if (!show || !companyId || selectedAgent) return;
    const q = agentQuery.trim();
    if (q.length < 2) {
      setAgentSearchResults([]);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setAgentSearchLoading(true);
      try {
        const res = await MemberService.getAllMembers({
          limit: 20,
          offset: 0,
          active_only: true,
          type: 'AGENT',
          company_id: companyId,
          search_term: q
        });
        if (!cancelled && res.success) {
          setAgentSearchResults(Array.isArray(res.data) ? res.data : []);
        }
      } catch (e) {
        if (!cancelled) setAgentSearchResults([]);
      } finally {
        if (!cancelled) setAgentSearchLoading(false);
      }
    };

    const id = setTimeout(run, 280);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [show, companyId, agentQuery, selectedAgent]);

  const submit = async (e) => {
    e.preventDefault();
    setGlobalError('');
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        company_id: parseInt(companyId, 10),
        agent_id: selectedAgent.id,
        dependents: dependents.map((dep) => ({
          nom: dep.nom.trim(),
          prenom: dep.prenom.trim(),
          type: dep.type,
          sexe: dep.sexe || null,
          date_naissance: dep.date_naissance || null
        }))
      };
      const res = await receptionService.createHousehold(payload);
      if (!res.success) throw new Error(res.message || 'Création impossible');

      toast('success', 'Foyer (agent + ayants droit) créé avec succès.');
      if (typeof onCreated === 'function') onCreated(res.data);
      close();
    } catch (err) {
      setGlobalError(err.message || 'Création impossible');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="outline-secondary" className="recv-btn-ghost" onClick={close} disabled={saving}>
        Annuler
      </Button>
      <Button type="submit" form="household-form" variant="primary" disabled={!canSubmit}>
        {saving ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" aria-hidden />
            Enregistrement…
          </>
        ) : (
          'Enregistrer le foyer'
        )}
      </Button>
    </>
  );

  return (
    <ReceptionModalShell
      show={show}
      onHide={close}
      disableClose={saving}
      size="xl"
      dialogClassName="recv-modal-dialog--household"
      title="Nouveau foyer"
      subtitle="Rattachez des ayants droit à un agent SMIE existant pour la société choisie."
      footer={footer}
    >
      <Form id="household-form" onSubmit={submit} className="recv-form-modern">
        {globalError && (
          <Alert variant="danger" className="recv-modal-alert">
            {globalError}
          </Alert>
        )}

        <Row className="g-4">
          <Col lg={8}>
            <ReceptionModalSection
              title="Société &amp; agent titulaire"
              description="L'agent doit déjà exister dans l'annuaire de la société."
            >
              <Row className="g-3">
                <Col md={6}>
                  <ReceptionFormField
                    label="Société partenaire"
                    htmlFor="household-company"
                    required
                    error={errors.company_id}
                  >
                    <Form.Select
                      id="household-company"
                      value={companyId}
                      onChange={(e) => {
                        setCompanyId(e.target.value);
                        setSelectedAgent(null);
                        setAgentQuery('');
                        setAgentSearchResults([]);
                      }}
                      isInvalid={!!errors.company_id}
                      aria-required="true"
                    >
                      <option value="">Sélectionner une société…</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </ReceptionFormField>
                </Col>
              </Row>

              {!companyId && (
                <Alert variant="info" className="recv-modal-alert py-2 small mb-0">
                  Choisissez d&apos;abord la société, puis recherchez l&apos;agent par matricule ou nom.
                </Alert>
              )}

              {companyId && (
                <>
                  <Row className="g-3 mt-1">
                    <Col md={8}>
                      <ReceptionFormField
                        label="Rechercher l'agent"
                        htmlFor="household-agent-search"
                        required
                        error={errors.agent_id}
                        hint="Minimum 2 caractères (matricule, nom ou prénom)"
                      >
                        <ReceptionSearchAnchor>
                          <Form.Control
                            id="household-agent-search"
                            value={agentQuery}
                            onChange={(e) => {
                              setAgentQuery(e.target.value);
                              setSelectedAgent(null);
                            }}
                            placeholder="Ex. matricule ou nom de famille…"
                            isInvalid={!!errors.agent_id}
                            autoComplete="off"
                            aria-required="true"
                            aria-controls="household-agent-list"
                            aria-expanded={
                              !selectedAgent &&
                              agentQuery.trim().length >= 2 &&
                              (agentSearchLoading || agentSearchResults.length > 0)
                            }
                          />
                          <ReceptionFloatingResults
                            listId="household-agent-list"
                            show={!selectedAgent && agentQuery.trim().length >= 2}
                            loading={agentSearchLoading}
                            emptyMessage="Aucun agent trouvé pour cette société"
                          >
                            {agentSearchResults.map((agent) => (
                              <ReceptionFloatingResultItem
                                key={agent.id}
                                primary={`${agent.prenom || ''} ${agent.nom || ''}`.trim()}
                                meta={agent.matricule ? `Matricule ${agent.matricule}` : undefined}
                                actionLabel="Sélectionner"
                                onSelect={() => {
                                  setSelectedAgent(agent);
                                  setAgentSearchResults([]);
                                  setAgentQuery(
                                    `${agent.prenom || ''} ${agent.nom || ''}`.trim() ||
                                      agent.matricule ||
                                      ''
                                  );
                                }}
                              />
                            ))}
                          </ReceptionFloatingResults>
                        </ReceptionSearchAnchor>
                      </ReceptionFormField>
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                      {selectedAgent && (
                        <Button
                          variant="outline-secondary"
                          className="w-100 recv-btn-ghost"
                          type="button"
                          onClick={() => {
                            setSelectedAgent(null);
                            setAgentQuery('');
                            setAgentSearchResults([]);
                          }}
                        >
                          Changer d&apos;agent
                        </Button>
                      )}
                    </Col>
                  </Row>

                  {selectedAgent && (
                    <div className="recv-modal-selected-card mt-2" role="status">
                      <span className="recv-modal-selected-card__label">Agent sélectionné</span>
                      <strong>
                        {selectedAgent.prenom} {selectedAgent.nom}
                      </strong>
                      {selectedAgent.matricule && (
                        <span className="font-monospace text-muted ms-2">{selectedAgent.matricule}</span>
                      )}
                    </div>
                  )}

                  {companyId && !selectedAgent && (
                    <Alert variant="warning" className="recv-modal-alert py-2 small mt-2 mb-0">
                      Sélectionnez un agent avant d&apos;ajouter les ayants droit.
                    </Alert>
                  )}
                </>
              )}
            </ReceptionModalSection>

            {selectedAgent && (
              <ReceptionModalSection
                title="Ayants droit"
                description="Conjoint, conjointe ou enfants rattachés à l'agent."
              >
                <DependentsFieldArray value={dependents} onChange={setDependents} errors={errors} />
              </ReceptionModalSection>
            )}
          </Col>

          <Col lg={4}>
            <aside className="recv-modal-summary" aria-label="Résumé du foyer">
              <h3 className="recv-modal-summary__title">Résumé</h3>
              <ul className="recv-modal-summary__list">
                <li>
                  <span className="recv-modal-summary__key">Société</span>
                  <span className="recv-modal-summary__val">
                    {companyId
                      ? companies.find((c) => String(c.id) === String(companyId))?.name || '—'
                      : <span className="text-muted">À choisir</span>}
                  </span>
                </li>
                <li>
                  <span className="recv-modal-summary__key">Agent</span>
                  <span className="recv-modal-summary__val">
                    {selectedAgent ? (
                      <>
                        {selectedAgent.prenom} {selectedAgent.nom}
                      </>
                    ) : (
                      <span className="text-muted">Non sélectionné</span>
                    )}
                  </span>
                </li>
                <li>
                  <span className="recv-modal-summary__key">Ayants droit</span>
                  <span className="recv-modal-summary__val">
                    {dependents.length === 0 ? (
                      <span className="text-muted">Aucun (optionnel)</span>
                    ) : (
                      <strong>{dependents.length}</strong>
                    )}
                  </span>
                </li>
              </ul>
              <p className="recv-modal-summary__hint small text-muted mb-0">
                Les matricules des ayants droit sont générés automatiquement à l&apos;enregistrement.
              </p>
            </aside>
          </Col>
        </Row>
      </Form>
    </ReceptionModalShell>
  );
}
